import React, { FC, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { billingService } from "../../../../api";
import { Container, InfoSection, InfoRow, InfoLabel, InfoValue, CyclesSection } from './styles';
import { HeadLine1, BodyDefault } from "../../../../components/typography/styled";
import { Button, Table, TopProgress } from "../../../../components";
import ModalVariables from "../../../../components/modal-alerts";
import Skeleton from 'react-loading-skeleton';
import { useTheme } from "styled-components";
import { useUi } from "../../../../context/ui.context";
import { icons } from "../../../../icons";

const ProductPage:FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const theme:any = useTheme();
  const { viewAlert } = useUi();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Modal state
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalComplete, setModalComplete] = useState<boolean>(false);
  const [dataToEdit, setDataToEdit] = useState<any>(null);
  const [alertSubTitle, setAlertSubTitle] = useState<string>("");

  const fetchProduct = useCallback(() => {
    if (productId) {
      billingService.getProduct(productId)
        .then((res) => {
          setProduct(res.data);
          setLoading(false);
        })
        .catch((e) => {
          console.log('error', e);
          setLoading(false);
        });
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const closeModalCancel = useCallback(() => {
    setViewModal(false);
    setActiveAnimation(false);
    setModalType(null);
    setModalTitle("");
    setModalLoading(false);
    setModalComplete(false);
    setDataToEdit(null);
    setAlertSubTitle("");
  }, []);

  const handleDelete = () => {
    setModalType('universal');
    setModalTitle(t("DELETE_PRODUCT") as string);
    setAlertSubTitle(t("DELETE_PRODUCT_CONFIRM") as string);
    setViewModal(true);
    setActiveAnimation(true);
  };

  const handleAddCycle = () => {
    setModalType('product-cycle-edit');
    setDataToEdit({ product });
    setViewModal(true);
    setActiveAnimation(true);
  };

  const handleEditCycle = (cycle: any) => {
    setModalType('product-cycle-edit');
    setDataToEdit({ product, productCycle: cycle });
    setViewModal(true);
    setActiveAnimation(true);
  };

  const handleDeleteCycle = (cycle: any) => {
    setModalType('universal');
    setModalTitle(t("DELETE_CYCLE") as string);
    setAlertSubTitle(t("DELETE_CYCLE_CONFIRM") as string);
    setDataToEdit({ cycleToDelete: cycle });
    setViewModal(true);
    setActiveAnimation(true);
  };

  const handleModalSuccess = useCallback(async (result?: any) => {
    // Product cycle edit modal returns true on success
    if (modalType === 'product-cycle-edit') {
      if (result === true) {
        viewAlert({ severity: 'success', message: t("CYCLE_SAVED") });
        closeModalCancel();
        fetchProduct();
      }
      return;
    }

    setModalLoading(true);
    try {
      if (modalType === 'universal' && dataToEdit?.cycleToDelete) {
        // Delete cycle
        await billingService.deleteProductCycle(dataToEdit.cycleToDelete.id);
        viewAlert({ severity: 'success', message: t("CYCLE_DELETED") });
        setModalComplete(true);
        setTimeout(() => {
          closeModalCancel();
          fetchProduct();
        }, 400);
      } else if (modalType === 'universal' && productId) {
        // Delete product
        await billingService.deleteProduct(productId);
        viewAlert({ severity: 'success', message: t("PRODUCT_DELETED") });
        setModalComplete(true);
        setTimeout(() => {
          closeModalCancel();
          navigate('/billing/products');
        }, 400);
      }
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t("ERROR_OCCURRED")
      });
      setModalComplete(false);
    } finally {
      setModalLoading(false);
    }
  }, [modalType, productId, dataToEdit, closeModalCancel, navigate, t, viewAlert, fetchProduct]);

  if (loading) {
    return (
      <Container>
        <TopProgress loading={loading}/>
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={250} style={{marginBottom:"10px"}} height={30}
        />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={'100%'} height={200}
        />
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <BodyDefault>{t("PRODUCT_NOT_FOUND")}</BodyDefault>
      </Container>
    );
  }

  const priceHeading = product.price_model === 'dynamic_or_fixed'
    ? t("MINIMUM_PRICE")
    : t("FIXED_PRICE");

  const cyclesColumns = [
    { title: t("DISPLAY_NAME"), key: 'display_name' },
    { title: priceHeading, key: 'fixed_price' },
    { title: t("SETUP_FEE"), key: 'setup_fee' },
    { title: t("RELATIVE_PRICE"), key: 'is_relative_price' },
    { title: t("STATUS"), key: 'status' },
    { title: '', key: 'props' },
  ];

  // Build a map of original cycle data by id for use in actions
  const cyclesMap: Record<number, any> = {};
  product.cycles?.forEach((cycle: any) => {
    cyclesMap[cycle.id] = cycle;
  });

  const cyclesData = product.cycles?.map((cycle:any) => ({
    ...cycle,
    display_name: cycle.display_name || '-',
    fixed_price: cycle.fixed_price ? `${cycle.fixed_price} ${cycle.currency || ''}` : '0.00',
    setup_fee: cycle.setup_fee ? `${cycle.setup_fee} ${cycle.currency || ''}` : '0.00',
    is_relative_price: cycle.is_relative_price ? t("YES") : t("NO"),
    status: cycle.status || '-',
  })) || [];

  const cycleSettings = [
    {
      title: t("EDIT"),
      onClick: (row: any) => handleEditCycle(cyclesMap[row.id] || row),
      icon: icons.Edit,
    },
    {
      title: t("DELETE"),
      onClick: (row: any) => handleDeleteCycle(cyclesMap[row.id] || row),
      icon: icons.DeleteIcon,
    },
  ];

  const isFreeProduct = product.price_model === 'free';

  return(
    <Container>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <div>
          <HeadLine1>{product.name}</HeadLine1>
          {product.description && <BodyDefault>{product.description}</BodyDefault>}
          {product.status && <BodyDefault>{t("STATUS")}: {product.status}</BodyDefault>}
        </div>
        <div style={{display: 'flex', gap: '10px'}}>
          <Button
            variant="primary"
            size="display"
            onClick={() => navigate(`/billing/products/${productId}/edit`)}
            title={t("EDIT")}
          />
          {!isFreeProduct && (
            <Button
              variant="primary"
              size="display"
              onClick={handleAddCycle}
              title={t("ADD_CYCLE")}
            />
          )}
          <Button
            variant="stroke"
            size="display"
            onClick={handleDelete}
            title={t("DELETE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate('/billing/products')}
            title={t("BACK_TO_PRODUCTS")}
          />
        </div>
      </div>

      <InfoSection>
        <HeadLine1 style={{fontSize: '18px', marginBottom: '15px'}}>{t("OVERVIEW")}</HeadLine1>
        <InfoRow>
          <InfoLabel>{t("PRODUCT_CODE")}:</InfoLabel>
          <InfoValue>{product.code || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("PRODUCT_GROUP")}:</InfoLabel>
          <InfoValue>{product.group?.name || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("PRICE_MODEL")}:</InfoLabel>
          <InfoValue>{product.price_model_display || product.price_model || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("MODULE")}:</InfoLabel>
          <InfoValue>
            {product.module?.name || '-'}
            {product.module?.path && ` (${product.module.path})`}
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("STATUS")}:</InfoLabel>
          <InfoValue>{product.status || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("BILLING_TYPE")}:</InfoLabel>
          <InfoValue>{product.billing_type || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("AUTO_SETUP")}:</InfoLabel>
          <InfoValue>{product.auto_setup_display || product.auto_setup || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t("TAXABLE")}:</InfoLabel>
          <InfoValue>{product.taxable ? t("YES") : t("NO")}</InfoValue>
        </InfoRow>
      </InfoSection>

      <CyclesSection>
        <HeadLine1 style={{fontSize: '18px', marginBottom: '15px'}}>{t("PRODUCT_CYCLES")}</HeadLine1>
        {isFreeProduct ? (
          <BodyDefault style={{ opacity: 0.7 }}>
            {t("FREE_PRODUCT_NO_CYCLES")}
          </BodyDefault>
        ) : cyclesData.length > 0 ? (
          <Table
            columns={cyclesColumns}
            rows={cyclesData}
            hideCheckbox
            hideOther
            settings={cycleSettings}
          />
        ) : (
          <div>
            <BodyDefault style={{ color: theme.palette?.error || 'red', marginBottom: '8px' }}>
              {t("NO_BILLING_CYCLES")}
            </BodyDefault>
            <BodyDefault style={{ opacity: 0.7 }}>
              {t("NO_BILLING_CYCLES_HINT")}
            </BodyDefault>
          </div>
        )}
      </CyclesSection>

      <ModalVariables
        modalType={modalType as any}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={handleModalSuccess}
        closeModalCancel={closeModalCancel}
        alertTitle={modalTitle}
        alertSubTitle={alertSubTitle}
        dataToEdit={dataToEdit}
        loading={modalLoading}
        loadingComplete={modalComplete}
      />
    </Container>
  )
}

export default ProductPage
